import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Custom date adapter for Material datepicker
 * Formats dates as "DD MMMM YYYY" (e.g., "01 April 2025")
 */
@Injectable()
export class AppDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    if (!this.isValid(date)) {
      return '';
    }

    if (displayFormat === 'input') {
      const day = (date.getDate() < 10 ? '0' : '') + date.getDate();
      const month = this.getMonthNames('long')[date.getMonth()];
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    }

    return super.format(date, displayFormat);
  }

  override parse(value: unknown): Date | null {
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }

    const trimmed = value.trim();
    
    // Try standard date parsing first
    const standardDate = new Date(trimmed);
    if (this.isValid(standardDate)) {
      return standardDate;
    }

    // Parse "DD MMMM YYYY" or "D MMM YYYY"
    const parts = trimmed.split(/\s+/);
    if (parts.length !== 3) {
      return null;
    }

    const [dayStr, monthStr, yearStr] = parts;
    const day = Number(dayStr);
    const year = Number(yearStr);

    if (isNaN(day) || isNaN(year)) {
      return null;
    }

    // Find month index (try long names, then short names)
    const monthLower = monthStr.toLowerCase();
    let monthIndex = this.getMonthNames('long')
      .map(m => m.toLowerCase())
      .indexOf(monthLower);

    if (monthIndex === -1) {
      monthIndex = this.getMonthNames('short')
        .map(m => m.toLowerCase())
        .indexOf(monthLower);
    }

    if (monthIndex === -1) {
      return null;
    }

    const parsed = new Date(year, monthIndex, day);
    return this.isValid(parsed) ? parsed : null;
  }

  override isValid(date: Date | null): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }
}
